// 📅 Validação de data não-futura — regra geral do prontuário (B296/AI_135)
// ─────────────────────────────────────────────────────────────
// Bloqueia a inserção/edição de um registro clínico (consulta/nota, exame,
// marco de transplante/CHC, nascimento, etc.) com data FUTURA. Datas
// retroativas (passado) são permitidas.
//
// "Hoje" é calculado no fuso de Brasília (America/Sao_Paulo) para não bloquear
// indevidamente perto da meia-noite (o app roda em navegadores no Brasil; o
// UTC já "virou o dia" a partir das 21h locais).
//
// NÃO se aplica a: prescrições (data = emissão/assinatura, RDC 1000/2025),
// agenda/agendamentos (datas futuras são o objetivo) e filtros de busca.
// ─────────────────────────────────────────────────────────────

/** Data de hoje no fuso de Brasília, em ISO 'YYYY-MM-DD' (p/ comparar e p/ o atributo max dos inputs). */
export function hojeISO(): string {
  // 'en-CA' formata como 'YYYY-MM-DD'.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Extrai e normaliza a parte 'YYYY-MM-DD' (zero-padded) de um valor de data.
 * Aceita 'YYYY-MM-DD', ISO datetime 'YYYY-MM-DDTHH:MM' e também mês/dia com 1
 * dígito ('2026-7-5' — alguns campos de máscara montam sem padding). null se
 * não reconhecível. O padding garante comparação lexicográfica = cronológica.
 */
function soData(valor: string): string | null {
  const m = valor.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return null;
  return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
}

/**
 * true se `valor` é uma data válida ESTRITAMENTE depois de hoje (Brasília).
 * Vazio/indefinido/não-ISO → false (não bloqueia: o "campo vazio/formato" é
 * tratado pelas validações próprias de cada formulário). Hoje e passado → false.
 */
export function ehDataFutura(valor?: string | null): boolean {
  if (!valor) return false;
  const d = soData(String(valor));
  if (!d) return false;
  // Comparação lexicográfica de 'YYYY-MM-DD' (zero-padded) = comparação cronológica.
  return d > hojeISO();
}

/** Mensagem padrão de erro de data futura. */
export const MSG_DATA_FUTURA =
  "Data futura não é permitida. Informe a data real (hoje ou anterior).";

/** Mensagem opcionalmente prefixada pelo nome do campo (ex.: "Data do exame"). */
export function msgDataFutura(rotulo?: string): string {
  return rotulo
    ? `${rotulo}: data futura não é permitida. Informe a data real (hoje ou anterior).`
    : MSG_DATA_FUTURA;
}
